import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { Observable, asyncScheduler, tap } from 'rxjs';

import { ExtendedAutoSuggestions, SearchSuggestionService } from '@hdms/shared/page-frame/data-access';
import { BackdropService, fadeOnAddRemove } from '@hdms/shared/util-core';

import { SearchSuggestionKeyboardControlService } from '../../../services';
import { HeaderSearchInputComponent } from '../../header-search/search-input/search-input.component';

const SUGGESTION_FLYOUT_REOPEN_DELAY = 350;

@Component({
  selector: 'shrd-pf-ft-search-popover',
  templateUrl: './header-search-popover.component.html',
  styleUrls: ['./header-search-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeOnAddRemove('showFlyout', 0.25)],
  encapsulation: ViewEncapsulation.None
})
export class HeaderSearchPopoverComponent implements OnDestroy {
  hasFlyout = false;
  isFocused: boolean;

  readonly isLoading$ = this.suggestionService.isLoading$();
  readonly suggestions$ = this.getSuggestions$();
  readonly searchTerm$ = this.suggestionService.getSearchTerm$();

  @Output() readonly searchFocused = new EventEmitter<boolean>();

  @HostBinding('class.shrd-pf-ft-search-popover') private readonly hostClass = true;

  @HostBinding('class.has-focus')
  get hasFocus(): boolean {
    return this.isFocused;
  }

  set hasFocus(hasFocus: boolean) {
    if (this.isFocused !== hasFocus) {
      this.isFocused = hasFocus;
      this.searchFocused.emit(this.isFocused);
    }
  }

  @ViewChild(HeaderSearchInputComponent) private readonly searchInput: HeaderSearchInputComponent;
  @ViewChild('flyout', { read: ElementRef }) private readonly flyout: ElementRef<HTMLDivElement>;

  private hasTerm = false;

  @HostListener('keydown', ['$event'])
  private onKeyDown(event: KeyboardEvent): void {
    this.hasFlyout && this.keyboardControlService.handleKeyboardEvent(event);
  }

  constructor(
    private readonly suggestionService: SearchSuggestionService,
    private readonly keyboardControlService: SearchSuggestionKeyboardControlService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly backdrop: BackdropService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.removeBackdrop();
    this.hasFocus = false;
  }

  onInputFocusIn(): void {
    this.hasFocus = true;
    this.addBackdrop();

    asyncScheduler.schedule(() => this.openFlyout(), SUGGESTION_FLYOUT_REOPEN_DELAY);
  }

  onInputFocusOut(): void {
    if (this.document.hasFocus()) {
      if (this.flyout?.nativeElement.contains(this.document.activeElement)) {
        this.scheduleFocusInput();
        return;
      }

      this.closeFlyout();
    }
  }

  updateLinkNodes(linkNodes: NodeListOf<HTMLElement>): void {
    this.keyboardControlService.updateLinkNodes(linkNodes);
  }

  public restoreInitialState(): void {
    if (this.document.hasFocus()) {
      this.closeFlyout();
      this.hasFocus = false;
      this.removeBackdrop();
      this.cdr.markForCheck();
    }
  }

  private openFlyout(): void {
    if (this.hasTerm && this.hasFocus && !this.hasFlyout) {
      this.hasFlyout = true;
      this.cdr.markForCheck();
      this.scheduleFocusInput();
    }
  }

  private closeFlyout(): void {
    this.hasFlyout = false;
  }

  private getSuggestions$(): Observable<ExtendedAutoSuggestions> {
    return this.suggestionService.suggestions$.pipe(
      tap(({ hasValidTerm }) => {
        this.hasTerm = hasValidTerm;
        hasValidTerm ? this.openFlyout() : this.closeFlyout();
      })
    );
  }

  private addBackdrop(): void {
    this.backdrop.create({ hostClasses: ['z-fixed'] });
  }

  private removeBackdrop(): void {
    this.backdrop.remove();
  }

  private scheduleFocusInput(): void {
    asyncScheduler.schedule(() => this.searchInput.focus());
  }
}
